import {Network} from "../../../network/Network";
import {HttpStatus} from "../../../network/status/HttpStatus";
import {ErrorCode} from "../../../network/status/error/ErrorCode";
import {ProductType} from "../../../models/product/ProductType";
import {INetworkComponent} from "../../../network/types/INetworkComponent";
import {WebSocketCommunityEventName} from "../../../network/socket/names/WebSocketCommunityEventName";
import {arrayRemove} from "../../../sedestral/framework/utilities/ArrayRemove";
import {ICommunityAltImageModel} from "../../../models/community/alt/ICommunityAltImageModel";
import {ICommunityAltOverviewModel} from "../../../models/community/alt/ICommunityAltOverviewModel";
import {ICommunityAltScanEndModel} from "../../../models/community/alt/ICommunityAltScanEndModel";
import {Services} from "../../Services";
import {Resources} from "../../../resources/Resources";

export class CommunityAltService {

    private static imageEvents: ((image: ICommunityAltImageModel) => void)[] = [];
    private static overviewEvents: ((overview: ICommunityAltOverviewModel) => void)[] = [];
    private static scanEndEvents: ((scanEnd: ICommunityAltScanEndModel) => void)[] = [];

    public static init(): void {
        Network.on(ProductType.COMMUNITY, WebSocketCommunityEventName.COMMUNITY_ALT_IMAGE_UPDATE, (data) => {
            this.imageEvents.forEach(callback => callback(data));
        });

        Network.on(ProductType.COMMUNITY, WebSocketCommunityEventName.COMMUNITY_ALT_OVERVIEW_UPDATE, (data) => {
            this.overviewEvents.forEach(callback => callback(data));
        });

        Network.on(ProductType.COMMUNITY, WebSocketCommunityEventName.COMMUNITY_ALT_SCAN_END, (data) => {
            this.scanEndEvents.forEach(callback => callback(data));
        });
    }

    public static onScanEndEvent(component: INetworkComponent, func: (scanEnd: ICommunityAltScanEndModel) => void) {
        this.scanEndEvents.push(func);
        component.onRemove(() => arrayRemove(this.scanEndEvents, func));
    }

    public static onImageUpdateEvent(component: INetworkComponent, func: (image: ICommunityAltImageModel) => void) {
        this.imageEvents.push(func);
        component.onRemove(() => arrayRemove(this.imageEvents, func));
    }

    public static onOverviewUpdateEvent(component: INetworkComponent, func: (overview: ICommunityAltOverviewModel) => void) {
        this.overviewEvents.push(func);
        component.onRemove(() => arrayRemove(this.overviewEvents, func));
    }

    /**
     * Un traitement deja en cours renvoie 429 : sans ce message l'action semblerait sans effet.
     * Le 402 de quota est intercepte globalement par la modale de paiement, inutile de le traiter ici.
     */
    private static handleActionErrors(component?: INetworkComponent) {
        Services.handleErrors(component, [
            {status: HttpStatus.TOO_MANY_REQUESTS, message: Resources.t("words.altImagesAlreadyRunning")}
        ]);
    }

    public static async get(communityId: string, cursor?: string, component?: INetworkComponent): Promise<ICommunityAltOverviewModel> {
        const request = await Network.get(ProductType.COMMUNITY,
            `/community/alt/${communityId}${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ``}`, component);

        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async scan(communityId: string, component?: INetworkComponent): Promise<ICommunityAltOverviewModel> {
        this.handleActionErrors(component);

        // le jeton de scan renvoie TASK_IN_PROGRESS quand un parcours tourne deja ; cibler le code
        // et non le statut 400, sinon tout autre refus porterait ce message a tort
        Services.handleErrors(component, [
            {errorCode: ErrorCode.TASK_IN_PROGRESS, message: Resources.t("words.altImagesAlreadyRunning")}
        ]);

        const request = await Network.post(ProductType.COMMUNITY, "/community/alt/scan", {
            communityId: communityId
        }, component);

        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async generate(communityId: string, ids: string[], publish: boolean, component?: INetworkComponent): Promise<ICommunityAltOverviewModel> {
        this.handleActionErrors(component);

        const request = await Network.postJson(ProductType.COMMUNITY, "/community/alt/generate", {
            communityId: communityId,
            ids: ids,
            publish: publish
        }, component);

        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async publish(communityId: string, ids: string[], component?: INetworkComponent): Promise<ICommunityAltOverviewModel> {
        this.handleActionErrors(component);

        const request = await Network.postJson(ProductType.COMMUNITY, "/community/alt/publish", {
            communityId: communityId,
            ids: ids
        }, component);

        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async update(communityId: string, id: string, alt: string, component?: INetworkComponent): Promise<ICommunityAltImageModel> {
        const request = await Network.postJson(ProductType.COMMUNITY, "/community/alt/update", {
            communityId: communityId,
            id: id,
            alt: alt
        }, component);

        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async cancel(communityId: string, component?: INetworkComponent): Promise<ICommunityAltOverviewModel> {
        const request = await Network.post(ProductType.COMMUNITY, "/community/alt/cancel", {
            communityId: communityId
        }, component);

        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }
}
